import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PopupToBottomSheet from '@/shared-components/popups/components/PopupToBottomSheet';
import { getStickerId, getStickerImageId } from '@/shared-components/stickers/utils';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import { PROFILE_BADGES_BY_ID } from '../components/profileBadgesConfig';

import Inspiring from './inspiring';

import './index.css';

const extractStickers = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.stickers)) return payload.stickers;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeUsername = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/^@+/, '').trim();
};

const normalizeCreator = (creator, fallbackCreator = null) => {
  const source = creator && typeof creator === 'object'
    ? creator
    : (fallbackCreator && typeof fallbackCreator === 'object' ? fallbackCreator : null);
  if (!source) {
    return {
      creatorName: null,
      creatorUsername: null,
    };
  }

  const creatorUsername = normalizeUsername(source.user_username || source.channel_name || '');
  const creatorName = (typeof source.user_name === 'string' && source.user_name.trim())
    ? source.user_name.trim()
    : ((typeof source.channel_name === 'string' && source.channel_name.trim()) ? source.channel_name.trim() : creatorUsername || null);

  return {
    creatorName: creatorName || null,
    creatorUsername: creatorUsername || null,
  };
};

const normalizeSticker = (sticker, fallbackCreator = null) => {
  const imageId = getStickerImageId(sticker);
  const id = getStickerId(sticker);
  if (!imageId || !id) return null;

  const { creatorName, creatorUsername } = normalizeCreator(sticker?.creator, fallbackCreator);
  return {
    id: String(id),
    imageId,
    creatorName,
    creatorUsername,
  };
};

export default function ProfileStats({ fetchData }) {
  const { internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.stats');
  const { cacheHandler } = useStateHandler();
  const [openInspiring, setOpenInspiring] = useState(false); // Estado del popup
  const [isBadgePreviewOpen, setIsBadgePreviewOpen] = useState(false);
  const [selectedSticker, setSelectedSticker] = useState(null);

  const numberOfArtworks = useMemo(() => (
    Utils.numberParserMillionThousand(fetchData.user_profile_info.user_artworks)
  ), [fetchData.user_profile_info.user_artworks]);

  const numberOfInspiring = useMemo(() => (
    Utils.numberParserMillionThousand(fetchData.user_profile_info.user_inspiring ?? 0)
  ), [fetchData.user_profile_info.user_inspiring]);

  const numberOfLikes = useMemo(() => (
    Utils.numberParserMillionThousand(fetchData.user_profile_info.user_likes)
  ), [fetchData.user_profile_info.user_likes]);

  const onCloseClick = () => {
    setOpenInspiring(false); // Cierra el popup
    cacheHandler.removeFromCache(`${fetchData._id}_inspiring`);
    cacheHandler.removeFromCache(`${fetchData._id}_following`);
  };

  const selectedBadgeId = fetchData?.selected_badge_id;
  const selectedBadge = selectedBadgeId ? PROFILE_BADGES_BY_ID[selectedBadgeId] : null;
  const profileUsername = useMemo(
    () => normalizeUsername(fetchData?.user_username),
    [fetchData?.user_username]
  );
  const fallbackCreator = useMemo(() => ({
    user_name: fetchData?.user_name || null,
    user_username: profileUsername || null,
    channel_name: fetchData?.channel_name || null,
  }), [fetchData?.channel_name, fetchData?.user_name, profileUsername]);
  const selectedStickerFromProfile = useMemo(() => {
    if (!selectedBadgeId || selectedBadge) return null;
    const normalizedSelectionId = String(selectedBadgeId);
    const normalized = normalizeSticker(fetchData?.selected_sticker, fallbackCreator);
    if (!normalized || normalized.id !== normalizedSelectionId) {
      return null;
    }
    return normalized;
  }, [fallbackCreator, fetchData?.selected_sticker, selectedBadge, selectedBadgeId]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedBadgeId || selectedBadge) {
      setSelectedSticker(null);
      return () => {
        cancelled = true;
      };
    }

    if (selectedStickerFromProfile) {
      setSelectedSticker(selectedStickerFromProfile);
      return () => {
        cancelled = true;
      };
    }

    if (!profileUsername) {
      setSelectedSticker(null);
      return () => {
        cancelled = true;
      };
    }

    const request = internal
      ? UserService.getAllUserStickers('me')
      : UserService.getUserStickers(profileUsername);

    request
      .then(({ response, data }) => {
        if (cancelled || !response?.ok) {
          return;
        }

        const normalizedSelectionId = String(selectedBadgeId);
        const matched = extractStickers(data)
          .map((sticker) => normalizeSticker(sticker, fallbackCreator))
          .filter(Boolean)
          .find((sticker) => sticker.id === normalizedSelectionId);

        setSelectedSticker(matched || null);
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedSticker(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackCreator, internal, profileUsername, selectedBadge, selectedBadgeId, selectedStickerFromProfile]);

  const featuredImage = selectedBadge?.image
    || (selectedSticker ? `${config.app.imageServiceDomain}/${selectedSticker.imageId}/w=512` : null);
  const featuredName = selectedBadge?.name || 'Sticker';
  const featuredDescription = selectedBadge?.description || '';
  const stickerCreatorName = selectedSticker?.creatorName || fetchData?.user_name || profileUsername || 'User';
  const stickerCreatorUsername = selectedSticker?.creatorUsername || profileUsername || null;

  return (
    <>
      <div className="user-profile-profile-stats__container">
        <div className="user-profile-profile-stats__list">
          {(internal || numberOfArtworks > 0) && (
            <div className="user-profile-stats__stat">
              <Text className="user-profile-stats__stat-number font-size-large" paragraph>
                {numberOfArtworks}
              </Text>
              <Text className="user-profile-stats__stat-text font-size-medium" paragraph>
                {t('labels.artworks')}
              </Text>
            </div>
          )}

          {(internal || numberOfInspiring > 0) && (
            <div className="user-profile-stats__stat button" onClick={() => setOpenInspiring(true)}>
              <Text className="user-profile-stats__stat-number font-size-large" paragraph>
                {numberOfInspiring}
              </Text>
              <Text className="user-profile-stats__stat-text font-size-medium" paragraph>
                {t('labels.inspiring')}
              </Text>
            </div>
          )}

          {(internal || numberOfLikes > 0) && (
            <div className="user-profile-stats__stat">
              <Text className="user-profile-stats__stat-number font-size-large" paragraph>
                {numberOfLikes}
              </Text>
              <Text className="user-profile-stats__stat-text font-size-medium" paragraph>
                {t('labels.likes')}
              </Text>
            </div>
          )}
        </div>

        {featuredImage && (
          <>
            <button
              type="button"
              className="user-profile-profile-stats__sticker-badge"
              onClick={() => setIsBadgePreviewOpen(true)}
              aria-label={t('aria.view_featured_badge')}
            >
              <img src={featuredImage} alt={featuredName} />
            </button>
            <PopupToBottomSheet
              className="profile-badge-preview-popup"
              open={isBadgePreviewOpen}
              close={() => setIsBadgePreviewOpen(false)}
              popupOptions={{ opacity: true }}
              forcePopup
            >
              <div className="notifications-content__container profile-badge-preview__container">
                <div className="notifications-content__header profile-badge-preview__header">
                  <XIcon
                    className="notifications-content__header-close"
                    onClick={() => setIsBadgePreviewOpen(false)}
                  />
                </div>
                <div className="profile-badge-preview">
                  <img src={featuredImage} alt={featuredName} />
                  {selectedBadge && (
                    <>
                      <Text className="profile-badge-preview__name">
                        {featuredName}
                      </Text>
                      <Text className="profile-badge-preview__description">
                        {featuredDescription}
                      </Text>
                    </>
                  )}
                  {!selectedBadge && selectedSticker && (
                    <Text className="profile-badge-preview__name profile-badge-preview__sticker-credit">
                      Sticker created by{' '}
                      {stickerCreatorUsername ? (
                        <Link
                          to={`/user/${encodeURIComponent(stickerCreatorUsername)}`}
                          className="profile-badge-preview__sticker-creator-link"
                        >
                          {stickerCreatorName}
                        </Link>
                      ) : (
                        <span className="profile-badge-preview__sticker-creator-link">
                          {stickerCreatorName}
                        </span>
                      )}
                    </Text>
                  )}
                </div>
              </div>
            </PopupToBottomSheet>
          </>
        )}
      </div>
      {/* Popup Inspiring */}
      <Inspiring 
        open={openInspiring} 
        close={onCloseClick} 
        fetchData={fetchData} 
        numberOfInspiring={numberOfInspiring} 
      />
    </>
  );
}
